import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { WorkspacePremium } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';

interface QualificationTypeItem {
  id: string;
  name?: string;
}

interface QualificationItem {
  id: string;
  name?: string;
  verified?: boolean;
  type?: string;
  tutor?: { type: string; id: string };
  tutorName?: string;
  issuedDate?: string;
  expiryDate?: string;
  qualificationType?: { id?: string };
  typeName?: string;
}

interface TutorItem {
  id: string;
  legalEntity?: { firstName?: string; lastName?: string };
}

export const QualificationList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allQualifications } = useGetList('qualifications', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allQualificationTypes } = useGetList('qualification-types', { pagination: { page: 1, perPage: 1000 } });
  const { data: allTutors } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 } });

  const typeNameMap = new Map<string, string>();
  allQualificationTypes?.forEach((qt: QualificationTypeItem) => typeNameMap.set(qt.id, qt.name ?? ''));

  const tutorMap = new Map<string, string>();
  allTutors?.forEach((t: TutorItem) => {
    const name = `${t.legalEntity?.firstName ?? ''} ${t.legalEntity?.lastName ?? ''}`.trim();
    if (name) tutorMap.set(t.id, name);
  });

  const qualifications = allQualifications?.map((q: QualificationItem) => ({
    ...q,
    typeName: typeNameMap.get(q.qualificationType?.id ?? '') || q.type,
    tutorName: q.tutor?.id ? tutorMap.get(q.tutor.id) || '—' : '—',
  })).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allQualifications?.length ?? 0,
    verified: allQualifications?.filter((q: QualificationItem) => q.verified === true).length ?? 0,
    pending: allQualifications?.filter((q: QualificationItem) => q.verified !== true).length ?? 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Qualifications"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Verified', value: String(stats.verified), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tutor</InputLabel>
          <Select
            value={filters.tutorId || ''}
            label="Tutor"
            onChange={e => setFilters(prev => ({ ...prev, tutorId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allTutors?.map((t: TutorItem) => <MenuItem key={t.id} value={t.id}>{t.legalEntity?.firstName ?? ""} {t.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Issued Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Verified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {qualifications?.map((qual: QualificationItem) => (
              <TableRow
                key={qual.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/qualifications/${qual.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <WorkspacePremium sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>{qual.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Chip label={qual.typeName} size="small" variant="outlined" /></TableCell>
                <TableCell><Typography variant="body2">{qual.tutorName || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{qual.issuedDate || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{qual.expiryDate || '—'}</Typography></TableCell>
                <TableCell>
                  <Chip
                    label={qual.verified ? 'Verified' : 'Pending'}
                    size="small"
                    color={qual.verified ? 'success' : 'warning'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allQualifications?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!qualifications || qualifications.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <WorkspacePremium sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No qualifications found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
